/**
 * BlockOverridePanel — the body of «Override data for this run» (HB-5, mock V1).
 *
 * The operator who created the strategy through the eleven-step wizard learned
 * ONE vocabulary: theses, social proof, variables. This panel is that vocabulary
 * on the block-update dialog — the wizard's two selection tables behind a
 * counted segmented switch (approved variant V1: one table at a time, so the
 * dialog stays one screen tall), plus the read-only variables fact.
 *
 * Audiences, actors and the entity tree are gone: on a wizard-created strategy
 * the server discarded everything they sent (designer invariant 1 — nothing
 * editable that the server throws away).
 *
 * Props-in / JSX-out: every rule (what is preloaded, what counts as changed,
 * what the request carries) lives in `BlockScreenGenerationDialog` and
 * `blockOverrideSelection`. The tables are the wizard's own `SelectionTable`
 * recipe (invariant 24), with the wizard's thesis column budget imported rather
 * than struck a second time.
 */
import React, { useMemo } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, Stack, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import type { SDataTableColumnDef } from '@modules/ui-components/dialogs/SDataTable';
import { STRATEGY_CANVAS } from '@test-kit/selectors/strategy-canvas';
import { SelectionTable } from './strategyWizard/SelectionTable';
import {
  INSIGHT_COLUMN_SIZES,
  INSIGHT_THESIS_MAX_LINES,
} from './strategyWizard/steps/InsightsStep';
import type {
  InsightThesisSelectionRow,
  SocialProofRow,
  WizardSourcePlane,
} from './strategyWizard/useWizardSourceRows';
import type { StarvedInsightScope } from './blockOverrideSelection';

export type BlockOverrideSegment = 'theses' | 'social-proof';

/** The strategy's variable package, as the accordion states it read-only. */
export interface BlockOverrideVariables {
  /** Absent when the head carries no variable package manifest. */
  insightVariableCount?: number;
  projectVariableCount?: number;
  ids: string[];
  /**
   * F-LIVE2-41: WHICH rule produced `ids` — the resolver's own
   * `variablePackageSource` words. `head-package` = the frozen manifest (what
   * the run sends); `head-variables` = the head's bare record, the legacy
   * fallback the caption must admit to; `unreconstructed` = a frozen manifest
   * whose own `finalVariableCount` the client could not reproduce, which the
   * caption admits to rather than stating a package it cannot vouch for (fix
   * round 1, review Minor #7).
   */
  source?: 'head-package' | 'head-variables' | 'none' | 'unreconstructed';
}

export interface BlockOverridePanelProps {
  segment: BlockOverrideSegment;
  onSegmentChange: (segment: BlockOverrideSegment) => void;

  theses: WizardSourcePlane<InsightThesisSelectionRow>;
  thesisSelectedIds: string[];
  onThesisSelectedIdsChange: (ids: string[]) => void;
  /** Thesis ids this block cited — the chip and the sort, never the tick. */
  citedThesisIds: Set<string>;

  socialProof: WizardSourcePlane<SocialProofRow>;
  socialProofSelectedIds: string[];
  onSocialProofSelectedIdsChange: (ids: string[]) => void;
  citedSocialProofIds: Set<string>;

  /**
   * Ruling HB5-R1: the scopes this tick left with nothing. Each one refuses the
   * run inline, names itself and carries its own way out (invariant 26); the
   * dialog's primary is disabled for exactly as long as this list is non-empty.
   */
  starvedScopes: StarvedInsightScope[];
  /** Re-tick every thesis of ONE scope — the refusal's one click. */
  onSelectScopeTheses: (scopeId: string) => void;
  /** True while the tick differs from the preload — the run is an override. */
  dirty: boolean;
  onReset: () => void;
  /** False when this block cited nothing: the preload is explained instead. */
  hasCitations: boolean;
  /** True when the head froze a narrowing (false = the analysis' projection). */
  narrowed: boolean;
  /**
   * S-7: the banner states the C7-1 NO-OVERRIDE baseline — how many theses the
   * run uses with the dialog untouched. It must never recompute from the current
   * tick, or the one sentence that describes the untouched run starts describing
   * the touched one («…the same 0 theses the run would use with the dialog
   * untouched» while the badge says «Override active»).
   */
  preloadedThesisCount: number;
  variables: BlockOverrideVariables;
  disabled?: boolean;
}

const PROVENANCE_LABEL = 'from AI Data Usage';

/** Chips beyond this fold into «+N more» — a package can carry dozens. */
export const VARIABLE_CHIP_LIMIT = 12;

const PLANE_LABEL: Record<SocialProofRow['plane'], string> = {
  review: 'Review',
  rating: 'Rating',
  studyCase: 'Study case',
  bigNumber: 'Big number',
};

/** `Review · 4.8★` → `4.8★`: the kind has its own column (mock V1). */
const sourceOf = (detail: string | undefined): string => {
  const value = detail ?? '';
  const separator = value.indexOf(' · ');
  return separator >= 0 ? value.slice(separator + 3) : value;
};

/**
 * Invariant 4: the chip sits OUTSIDE the clamped text, so the two-line ellipsis
 * can never eat the provenance.
 */
const ProvenanceChip: React.FC = () => (
  <Chip
    size="small"
    variant="outlined"
    label={PROVENANCE_LABEL}
    sx={{ flexShrink: 0, height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 'caption.fontSize' } }}
  />
);

export const BlockOverridePanel: React.FC<BlockOverridePanelProps> = ({
  segment,
  onSegmentChange,
  theses,
  thesisSelectedIds,
  onThesisSelectedIdsChange,
  citedThesisIds,
  socialProof,
  socialProofSelectedIds,
  onSocialProofSelectedIdsChange,
  citedSocialProofIds,
  starvedScopes,
  onSelectScopeTheses,
  dirty,
  onReset,
  hasCitations,
  narrowed,
  preloadedThesisCount,
  variables,
  disabled = false,
}) => {
  const thesisColumns = useMemo<SDataTableColumnDef<InsightThesisSelectionRow>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Thesis',
      ...INSIGHT_COLUMN_SIZES.thesis,
      grow: true,
      Cell: ({ row }) => (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            title={row.original.name}
            sx={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: INSIGHT_THESIS_MAX_LINES,
              overflow: 'hidden',
            }}
          >
            {row.original.name}
          </Typography>
          {citedThesisIds.has(row.original.id) && <ProvenanceChip />}
        </Stack>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Category',
      ...INSIGHT_COLUMN_SIZES.category,
      grow: false,
      Cell: ({ row }) => (
        <Typography variant="body2" color="text.secondary" noWrap title={row.original.category}>
          {row.original.category}
        </Typography>
      ),
    },
    {
      accessorKey: 'actorCount',
      header: 'Actors',
      ...INSIGHT_COLUMN_SIZES.actors,
      grow: false,
      Cell: ({ row }) => (
        <Typography variant="body2" color="text.secondary">{row.original.actorCount}</Typography>
      ),
    },
    {
      accessorKey: 'scopeLabel',
      header: 'Scope',
      ...INSIGHT_COLUMN_SIZES.scope,
      grow: false,
      Cell: ({ row }) => (
        <Chip
          label={row.original.scopeLabel || 'Global'}
          size="small"
          variant="outlined"
          title={row.original.scopeLabel}
          sx={{ minWidth: 0, maxWidth: '100%' }}
        />
      ),
    },
    {
      accessorKey: 'cumulativeWeight',
      header: 'Weight',
      ...INSIGHT_COLUMN_SIZES.weight,
      grow: false,
      Cell: ({ row }) => (
        <Typography variant="body2" color="text.secondary">
          {Number.isInteger(row.original.cumulativeWeight)
            ? String(row.original.cumulativeWeight)
            : row.original.cumulativeWeight.toFixed(1)}
        </Typography>
      ),
    },
  ], [citedThesisIds]);

  const socialProofColumns = useMemo<SDataTableColumnDef<SocialProofRow>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Item',
      Cell: ({ row }) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap title={row.original.name}>{row.original.name}</Typography>
          {citedSocialProofIds.has(row.original.id) && <ProvenanceChip />}
        </Stack>
      ),
    },
    {
      accessorKey: 'plane',
      header: 'Kind',
      Cell: ({ row }) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {PLANE_LABEL[row.original.plane]}
        </Typography>
      ),
    },
    {
      accessorKey: 'detail',
      header: 'Source',
      Cell: ({ row }) => (
        <Typography variant="body2" color="text.secondary" noWrap title={row.original.detail}>
          {sourceOf(row.original.detail)}
        </Typography>
      ),
    },
  ], [citedSocialProofIds]);

  const thesisSummary = theses.rows.length === 0
    ? ''
    : `${thesisSelectedIds.length} of ${theses.rows.length} selected · ${dirty
      ? 'the run is grounded on the selected theses'
      : 'this is what the run sends — nothing is overridden yet'}`;

  /**
   * REVIEW tier 2 (should-fix): the line must count what the row SHOWS. The
   * manifest's insight/project counts describe the pools the package was built
   * from and can exceed the ids it actually carries, so the split sentence is
   * used only while the two agree; otherwise the chips are the truth.
   */
  const splitMatchesIds = typeof variables.insightVariableCount === 'number'
    && typeof variables.projectVariableCount === 'number'
    && variables.insightVariableCount + variables.projectVariableCount === variables.ids.length;
  const variablesSentence = splitMatchesIds
    ? `${variables.insightVariableCount} insight variables · ${variables.projectVariableCount} project variables`
    : `${variables.ids.length} strategy variable${variables.ids.length === 1 ? '' : 's'}`;
  /**
   * F-LIVE2-41: the caption names WHERE the list came from, so neither a legacy
   * head nor a manifest the client could not reproduce passes a bare variables
   * record off as «the package the run sends». Copy only — no markup.
   */
  const variablesCaption = variables.source === 'head-variables'
    ? 'The strategy carries no frozen variable package — this is its variables record, '
      + 'which the run resolves from. Variables are not overridable here — change them on the strategy.'
    : variables.source === 'unreconstructed'
      ? 'The package count could not be reconstructed from the strategy’s frozen manifest, so this '
        + 'is its variables record — the run sends its own package. Variables are not overridable '
        + 'here — change them on the strategy.'
      : 'The run sends the strategy’s resolved variable package. Variables are not overridable '
        + 'here — change them on the strategy.';
  /** A long package is clamped, and the clamp SAYS so — never a silent trim. */
  const shownVariableIds = variables.ids.slice(0, VARIABLE_CHIP_LIMIT);
  const hiddenVariableCount = variables.ids.length - shownVariableIds.length;

  return (
    <Stack spacing={2}>
      {/* Invariant 26: the blocker sits ABOVE the thing it invalidates, names
          the scope in the feasibility contract's own words, and carries the one
          click that ends it (FP-6: no dead ends). Ruling HB5-R1. */}
      {starvedScopes.length > 0 && (
        <Alert severity="error" data-testid={STRATEGY_CANVAS.blockOverrideStarved}>
          <AlertTitle sx={{ mb: 0.5 }}>This selection cannot be generated from</AlertTitle>
          {starvedScopes.map((scope) => (
            <Box
              key={scope.scopeId}
              data-testid={STRATEGY_CANVAS.blockOverrideStarvedScope(scope.scopeId)}
              sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}
            >
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{scope.message}</Typography>
              <Button
                size="small"
                variant="outlined"
                /**
                 * MF-3 + MF-4 (invariant 8): tone text takes the Alert's OWN
                 * colour pair, never a raw `*.main` / `*.dark` token — that pair
                 * is the only one defined per mode. `error.main` failed light
                 * (#d32f2f on #fdeded = 4.39:1) and `error.dark` then failed
                 * dark (#d32f2f on rgb(22,11,11) = 3.88:1, DARKER than the
                 * surface it must contrast with).
                 *
                 * F-LIVE2-43 (design re-review after fix round 3): what is
                 * PAINTED, measured. Light inherits the Alert's own pair —
                 * #5f2120 on #fdeded = 10.73:1, the value the `AlertTitle` above
                 * it measures at. Dark does NOT inherit: the dark theme's
                 * `MuiButton` colour beats `color: inherit`, so the label paints
                 * `text.primary` (#deddff) on rgb(22,11,11) = 14.62:1. Both
                 * clear AA with room; the earlier comment named #f4c7c7 /
                 * 12.74:1 in dark, a pair this button never renders.
                 */
                color="inherit"
                disabled={disabled}
                onClick={() => onSelectScopeTheses(scope.scopeId)}
                data-testid={STRATEGY_CANVAS.blockOverrideStarvedFix(scope.scopeId)}
                sx={{
                  flexShrink: 0,
                  textTransform: 'none',
                  borderColor: 'currentColor',
                }}
              >
                {scope.fixLabel}
              </Button>
            </Box>
          ))}
        </Alert>
      )}

      {/* FP-7: the preload is explained where it could surprise — a block with
          no citations of its own still opens on exactly what the server sends. */}
      {!hasCitations && theses.rows.length > 0 && (
        <Alert
          severity="info"
          variant="outlined"
          role="status"
          data-testid={STRATEGY_CANVAS.blockOverrideNoCitations}
        >
          {`This block cites no data of its own. The table is preloaded from ${narrowed
            ? 'the strategy\'s frozen narrowing'
            : 'the analysis\' own projection'} — the same ${preloadedThesisCount} theses the run would use with the dialog untouched.`}
        </Alert>
      )}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={segment}
          onChange={(_event, value) => { if (value !== null) onSegmentChange(value as BlockOverrideSegment); }}
          aria-label="Override plane"
          data-testid={STRATEGY_CANVAS.blockOverrideSegment}
          // Designer invariant 13: the selected segment is a SOLID primary fill,
          // its focus ring in the contrast colour so it survives the fill.
          sx={{
            '& .MuiToggleButton-root': { textTransform: 'none' },
            '& .MuiToggleButton-root.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-focusVisible': {
                outline: '2px solid',
                outlineColor: 'primary.contrastText',
                outlineOffset: '-4px',
              },
            },
          }}
        >
          <ToggleButton
            value="theses"
            data-testid={STRATEGY_CANVAS.blockOverrideSegmentOption('theses')}
          >
            {`Insight theses ${thesisSelectedIds.length}/${theses.rows.length}`}
          </ToggleButton>
          <ToggleButton
            value="social-proof"
            data-testid={STRATEGY_CANVAS.blockOverrideSegmentOption('social-proof')}
          >
            {`Social proof ${socialProofSelectedIds.length}/${socialProof.rows.length}`}
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Invariant 3: one click puts both tables back on the preload. */}
        {dirty && (
          <Button
            size="small"
            variant="text"
            onClick={onReset}
            disabled={disabled}
            data-testid={STRATEGY_CANVAS.blockOverrideReset}
            sx={{ textTransform: 'none' }}
          >
            Reset to AI Data Usage
          </Button>
        )}
      </Stack>

      <Box hidden={segment !== 'theses'}>
        {theses.rows.length === 0 && !theses.loading && !theses.error ? (
          <Alert severity="info" variant="outlined">
            The pinned analysis carries no theses — there is nothing to narrow. This run is
            grounded on the strategy snapshot.
          </Alert>
        ) : (
          <SelectionTable<InsightThesisSelectionRow>
            rows={theses.rows}
            selectedIds={thesisSelectedIds}
            onSelectedIdsChange={onThesisSelectedIdsChange}
            columns={thesisColumns}
            loading={theses.loading}
            error={theses.error}
            disabled={disabled}
            searchPlaceholder="Search theses"
            summary={thesisSummary}
            columnLayout="grid"
            maxHeight={300}
            aria-label="Insight theses"
            data-testid={STRATEGY_CANVAS.blockOverrideTheses}
          />
        )}
      </Box>

      <Box hidden={segment !== 'social-proof'}>
        <SelectionTable<SocialProofRow>
          rows={socialProof.rows}
          selectedIds={socialProofSelectedIds}
          onSelectedIdsChange={onSocialProofSelectedIdsChange}
          columns={socialProofColumns}
          loading={socialProof.loading}
          error={socialProof.error}
          disabled={disabled}
          searchPlaceholder="Search social proof"
          summary={`${socialProofSelectedIds.length} of ${socialProof.rows.length} selected · the AI may cite only what is selected`}
          maxHeight={300}
          emptyState={(
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No reviews, ratings, study cases or big numbers on this project yet — generation
              works without them.
            </Typography>
          )}
          aria-label="Social proof"
          data-testid={STRATEGY_CANVAS.blockOverrideSocialProof}
        />
      </Box>

      {/* Invariant 1: a locked FACT, never a third selection surface. */}
      <Stack spacing={1} data-testid={STRATEGY_CANVAS.blockOverrideVariables}>
        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em', fontWeight: 600 }}>
            Variables
          </Typography>
          <Typography variant="caption" color="text.secondary">read-only · used by this block</Typography>
        </Stack>
        <Typography variant="body2">{variablesSentence}</Typography>
        {shownVariableIds.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {shownVariableIds.map((id) => (
              <Chip key={id} size="small" label={id} sx={{ fontFamily: 'monospace' }} />
            ))}
            {hiddenVariableCount > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label={`+${hiddenVariableCount} more`}
                data-testid={STRATEGY_CANVAS.blockOverrideVariablesMore}
              />
            )}
          </Stack>
        )}
        <Typography variant="caption" color="text.secondary">
          {variablesCaption}
        </Typography>
      </Stack>
    </Stack>
  );
};

BlockOverridePanel.displayName = 'BlockOverridePanel';
